import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { RefreshCw, BarChart3 } from 'lucide-react';
import { API_BASE, getAdminToken, formatAmount } from './adminUtils';
import { AllAccessPricingCard } from './AllAccessPricingCard';

export const RevenueTab = () => {
  const [revenueStats, setRevenueStats] = useState(null);
  const [revenueDays, setRevenueDays] = useState(30);
  const [loading, setLoading] = useState(false);

  const fetchRevenueStats = useCallback(async () => {
    const token = getAdminToken(); if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/revenue-stats?days=${revenueDays}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setRevenueStats(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [revenueDays]);

  useEffect(() => { fetchRevenueStats(); }, [revenueDays]);

  return (
    <div className="space-y-6">
      <AllAccessPricingCard />
      <Card className="bg-white/5 border-white/10 backdrop-blur">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2"><BarChart3 className="w-5 h-5 text-emerald-400" />Revenue</CardTitle>
          <div className="flex items-center gap-2">
            <select value={revenueDays} onChange={e => setRevenueDays(Number(e.target.value))} className="bg-white/10 border border-white/20 text-white rounded-md px-3 py-2">
              <option value={7} className="bg-slate-800">7 days</option>
              <option value={30} className="bg-slate-800">30 days</option>
              <option value={90} className="bg-slate-800">90 days</option>
            </select>
            <Button variant="outline" size="icon" onClick={fetchRevenueStats} className="border-white/20 text-white"><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 text-amber-500 animate-spin" /></div> : revenueStats ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border-emerald-500/20"><CardContent className="p-6"><p className="text-sm text-emerald-300">Period</p><p className="text-3xl font-bold text-white">{formatAmount(revenueStats.total_revenue)}</p><p className="text-xs text-emerald-400">{revenueStats.total_transactions} txns</p></CardContent></Card>
              <Card className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border-amber-500/20"><CardContent className="p-6"><p className="text-sm text-amber-300">All Time</p><p className="text-3xl font-bold text-white">{formatAmount(revenueStats.all_time_revenue)}</p></CardContent></Card>
              <Card className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border-purple-500/20"><CardContent className="p-6"><p className="text-sm text-purple-300">Avg</p><p className="text-3xl font-bold text-white">{revenueStats.total_transactions > 0 ? formatAmount(revenueStats.total_revenue / revenueStats.total_transactions) : '₹0'}</p></CardContent></Card>
            </div>
            <div className="rounded-xl border border-white/10 overflow-hidden">
              <Table>
                <TableHeader><TableRow className="border-white/10"><TableHead className="text-slate-300">Course</TableHead><TableHead className="text-slate-300 text-right">Revenue</TableHead><TableHead className="text-slate-300 text-right">Txns</TableHead></TableRow></TableHeader>
                <TableBody>{revenueStats.course_breakdown.map((c, i) => (<TableRow key={i} className="border-white/10"><TableCell className="text-white">{c.course}</TableCell><TableCell className="text-emerald-400 text-right">{formatAmount(c.revenue)}</TableCell><TableCell className="text-slate-300 text-right">{c.transactions}</TableCell></TableRow>))}</TableBody>
              </Table>
            </div>
          </div>
        ) : <p className="text-slate-400 text-center py-8">No data</p>}
      </CardContent>
    </Card>
    </div>
  );
};
